import {
  FormModal,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
} from '@/components/ui/form-modal';
import { type SourcingEventType } from '@/features/sourcing/types';

export interface AddEventFormData {
  title: string;
  eventType: SourcingEventType;
  deliveryZone: string;
  requiredDeliveryDate: string;
  bidDeadline: string;
  estimatedValue: string;
  description: string;
}

interface AddEventModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave: () => void;
  readonly formData: AddEventFormData;
  readonly setFormData: React.Dispatch<React.SetStateAction<AddEventFormData>>;
}

export function AddEventModal({
  isOpen,
  onClose,
  onSave,
  formData,
  setFormData,
}: AddEventModalProps) {
  return (
    <FormModal
      title="Create Sourcing Event"
      description="Create a new reverse auction or sealed proposal"
      isOpen={isOpen}
      onClose={onClose}
      onSave={onSave}
      saveLabel="Create Event"
      size="xl"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <FormField label="Title" required>
          <FormInput
            value={formData.title}
            onChange={(e) => setFormData({
              ...formData,
              title: e.target.value 
            })}
            placeholder="e.g., Q1 Seafood Procurement"
          />
        </FormField>
        <FormField label="Event Type" required>
          <FormSelect
            value={formData.eventType}
            onChange={(e) => setFormData({
              ...formData,
              eventType: e.target.value as SourcingEventType 
            })}
            options={[
              {
                value: 'sealed_proposal',
                label: 'Sealed Proposal' 
              },
              {
                value: 'reverse_auction',
                label: 'Reverse Auction' 
              },
            ]}
          />
        </FormField>
        <FormField label="Delivery Zone" required>
          <FormSelect
            value={formData.deliveryZone}
            onChange={(e) => setFormData({
              ...formData,
              deliveryZone: e.target.value 
            })}
            placeholder="Select zone..."
            options={[
              {
                value: 'Port of Miami',
                label: 'Port of Miami' 
              },
              {
                value: 'Barcelona Port',
                label: 'Barcelona Port' 
              },
              {
                value: 'Southampton',
                label: 'Southampton' 
              },
            ]}
          />
        </FormField>
        <FormField label="Estimated Value ($)">
          <FormInput
            type="number"
            min="0"
            value={formData.estimatedValue}
            onChange={(e) => setFormData({
              ...formData,
              estimatedValue: e.target.value 
            })}
            placeholder="50000"
          />
        </FormField>
        <FormField label="Bid Deadline" required>
          <FormInput
            type="datetime-local"
            value={formData.bidDeadline}
            onChange={(e) => setFormData({
              ...formData,
              bidDeadline: e.target.value 
            })}
          />
        </FormField>
        <FormField label="Required Delivery Date">
          <FormInput
            type="date"
            value={formData.requiredDeliveryDate}
            onChange={(e) => setFormData({
              ...formData,
              requiredDeliveryDate: e.target.value 
            })}
          />
        </FormField>
      </div>
      <FormField label="Description">
        <FormTextarea
          value={formData.description}
          onChange={(e) => setFormData({
            ...formData,
            description: e.target.value 
          })}
          placeholder="Event description and requirements..."
          rows={3}
        />
      </FormField>
    </FormModal>
  );
}
